import React, { useState } from "react";

interface GeneratedVideo {
  temp_id: string;
  platform: string;
  title: string;
  content: string;
  video_url: string;
  hashtags?: string[];
  call_to_action?: string;
}

interface GeneratedVideosProps {
  videos: GeneratedVideo[];
  onSave: (selectedVideos: GeneratedVideo[]) => void;
  isSaving?: boolean;
}

export const GeneratedVideos: React.FC<GeneratedVideosProps> = ({
  videos,
  onSave,
  isSaving = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(videos.map((v) => v.temp_id))
  );
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  const toggleSelect = (tempId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(tempId)) {
      newSelected.delete(tempId);
    } else {
      newSelected.add(tempId);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === videos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(videos.map((v) => v.temp_id)));
    }
  };

  const handleSave = () => {
    const selectedVideos = videos.filter((v) => selectedIds.has(v.temp_id));
    onSave(selectedVideos);
  };

  const getPlatformIcon = (platform: string) => {
    const platformLower = platform.toLowerCase();
    if (platformLower.includes("facebook")) return "facebook";
    if (platformLower.includes("instagram")) return "photo_camera";
    if (platformLower.includes("linkedin")) return "business";
    if (platformLower.includes("tiktok")) return "music_note";
    if (platformLower.includes("youtube")) return "videocam";
    return "campaign";
  };

  const getPlatformColor = (platform: string) => {
    return "bg-theme-surface-ui text-theme-fg border border-theme-border";
  };

  return (
    <div className="my-4 space-y-4">
      {/* Header with select all */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-theme-fg">
          Generated Videos
        </h3>
        <button
          onClick={toggleSelectAll}
          className="text-sm text-theme-primary hover:text-theme-primary-hover font-medium transition-colors"
        >
          {selectedIds.size === videos.length ? "Deselect All" : "Select All"}
        </button>
      </div>

      {/* Videos Grid - Reel Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((video) => {
          const isSelected = selectedIds.has(video.temp_id);
          const isPlaying = playingVideoId === video.temp_id;

          return (
            <div
              key={video.temp_id}
              className={`relative bg-theme-surface rounded-xl border-2 overflow-hidden transition-all ${
                isSelected
                  ? "border-theme-primary shadow-lg"
                  : "border-theme-border hover:border-theme-border-hover"
              }`}
            >
              {/* Selection Checkbox */}
              <div className="absolute top-2 left-2 z-10">
                <button
                  onClick={() => toggleSelect(video.temp_id)}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? "bg-theme-primary border-theme-primary"
                      : "bg-theme-surface border-theme-border"
                  }`}
                >
                  {isSelected && (
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Platform Badge */}
              <div className="absolute top-2 right-2 z-10">
                <div
                  className={`${getPlatformColor(
                    video.platform
                  )} rounded-full px-2 py-1 text-xs font-medium flex items-center gap-1`}
                >
                  <span className="material-icons text-xs">
                    {getPlatformIcon(video.platform)}
                  </span>
                  {video.platform}
                </div>
              </div>

              {/* Reel-Style Video Player */}
              <div className="relative bg-black" style={{ aspectRatio: "9/16" }}>
                <video
                  src={video.video_url}
                  className="w-full h-full object-contain"
                  controls={isPlaying}
                  onPlay={() => setPlayingVideoId(video.temp_id)}
                  onPause={() => setPlayingVideoId(null)}
                  onEnded={() => setPlayingVideoId(null)}
                  playsInline
                  muted={!isPlaying}
                />

                {/* Play Overlay (when not playing) */}
                {!isPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <button
                      onClick={() => {
                        const videoElement = document.querySelector(
                          `video[src="${video.video_url}"]`
                        ) as HTMLVideoElement;
                        videoElement?.play();
                      }}
                      className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                    >
                      <svg
                        className="ml-1 h-8 w-8 text-theme-fg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Video Info */}
              <div className="p-4">
                <h4 className="font-semibold text-theme-fg mb-2 line-clamp-2">
                  {video.title || "Untitled Video"}
                </h4>
                <p className="text-sm text-theme-fg-muted line-clamp-3 mb-3">
                  {video.content}
                </p>

                {/* Hashtags */}
                {video.hashtags && video.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {video.hashtags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="text-xs text-theme-primary bg-theme-primary/10 px-2 py-0.5 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {video.hashtags.length > 3 && (
                      <span className="text-xs text-theme-fg-muted">
                        +{video.hashtags.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between pt-4 border-t border-theme-border">
        <div className="text-sm text-theme-fg-muted">
          {selectedIds.size === 0 ? (
            "Select videos to save"
          ) : (
            <span className="font-medium text-theme-primary">
              {selectedIds.size} {selectedIds.size === 1 ? "video" : "videos"}{" "}
              selected
            </span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={selectedIds.size === 0 || isSaving}
          className="px-6 py-2.5 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
        >
          {isSaving ? (
            <>
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <span className="material-icons text-lg">save</span>
              Save Selected Videos
            </>
          )}
        </button>
      </div>
    </div>
  );
};
